<template>
    <div id="navDrawerContainer" :class="{ 'dark-theme': isDarkTheme }">
        <div class="logo inDrawer"></div>
        <h1 style="float: left; margin-top: -3px;">電廠維運</h1>
        <img class="user-avatar" :src="userAvatarUrl" @click="$emit('open-user-menu')" />

        <div style="clear: both;"></div>
        <div id="navDrawer">
            <!-- 組織選擇（固定區域） -->
            <div class="org-select">
                <Select v-model="currentOrgId" style="width: 100%;" transfer placement="bottom-start">
                    <Option v-for="org in organizations" :value="org.id" :key="org.id">
                        <div class="org-option">
                            <span class="org-name">{{ org.name }}</span>
                        </div>
                    </Option>
                </Select>
            </div>
            
            <!-- 操作模式選擇（固定區域） -->
            <div class="mode-selection">
                <div class="mode-buttons-grid">
                    <button
                        v-for="mode in processedModes"
                        :key="mode.key"
                        @click="handleModeClick(mode)"
                        :class="['mode-btn', { active: currentMode === mode.key, placeholder: mode.visualOnly, locked: mode.isLocked }]"
                        :title="mode.isLocked ? `${mode.title} (無權限)` : mode.title">
                        <i :class="['icofont', 'icofont-duotone', `icofont-${mode.icon}`]"></i>
                        <span>{{ mode.title }}</span>
                        <i v-if="mode.isLocked" class="icofont-lock lock-icon"></i>
                    </button>
                </div>
            </div>

            <div class="version-info">
                <span>版本: {{ version }}</span>
                <span>建置時間: {{ buildTime }}</span>
            </div>
        </div>
    </div>
</template>

<script>
import { Icon } from 'view-ui-plus';
import { eventBus } from '@shared/stores/globalState';
import { pageData } from '@shared/config/menuConfig';
import { navModeConfigs } from '@shared/config/pagePermissions';

// 導航模式配置（從 pagePermissions 統一定義）
const NAV_MODES = navModeConfigs;

export default {
    name: 'SideNavDrawer',
    components: {
        Icon
    },
    props: {
        organizations: {
            type: Array,
            required: true
        },
        selectedOrgId: {
            type: String,
            default: ''
        },
        userAvatarUrl: {
            type: String,
            required: true
        },
        theme: {
            type: String,
            default: 'light'
        },
        version: {
            type: String,
            required: true
        },
        buildTime: {
            type: String,
            required: true
        },
        availableModes: {
            type: Array,
            default: () => []
        }
    },
    emits: [
        'menu-select'
    ],
    data() {
        return {
            currentMode: 'operation',
            isDarkTheme: false
        };
    },
    computed: {
        currentOrgId: {
            get() {
                return this.selectedOrgId;
            },
            set(value) {
                // 調用異步方法處理組織切換
                this.handleOrgChange(value);
            }
        },
        // 處理模式列表，添加權限狀態（統一使用頁面權限機制）
        processedModes() {
            return NAV_MODES.map(mode => ({
                ...mode,
                hasAccess: this.availableModes.includes(mode.key),
                isLocked: !this.availableModes.includes(mode.key)
            }));
        },
        // 根據暗色主題自動切換 Menu 主題
        menuTheme() {
            return this.isDarkTheme ? 'dark' : 'light';
        },
        // 從 pageData 取得導航資料
        navData() {
            return pageData['nav'];
        }
    },
    mounted() {
        // 讀取保存的模式
        const savedMode = localStorage.getItem('navMode');
        if (savedMode) {
            this.currentMode = savedMode;
        }

        // 讀取保存的主題
        const savedTheme = localStorage.getItem('navTheme');
        if (savedTheme === 'dark') {
            this.isDarkTheme = true;
        }

        // 監聯外部的模式變化
        eventBus.on('ui:nav-mode-changed', this.handleModeChanged);
        // 監聽主題變化
        eventBus.on('ui:theme-changed', this.handleThemeChanged);
    },
    beforeUnmount() {
        eventBus.off('ui:nav-mode-changed', this.handleModeChanged);
        eventBus.off('ui:theme-changed', this.handleThemeChanged);
    },
    methods: {
        handleMenuSelect(event, item) {
            this.$emit('menu-select', event, item);
        },
        handleModeClick(mode) {
            if (mode.isLocked) {
                this.showLockedMessage(mode.title);
                return;
            }
            this.changeMode(mode.key);
        },
        changeMode(mode) {
            console.log('[SideNavDrawer] 切換模式:', mode);
            this.currentMode = mode;
            localStorage.setItem('navMode', mode);
            // 發送模式變化事件
            eventBus.emit('ui:nav-mode-change', mode);
            // 關閉 Drawer
            eventBus.emit('ui:close-nav-drawer');
        },
        showLockedMessage(title) {
            this.$Message?.info(`${title} 權限不足`);
        },
        handleModeChanged(mode) {
            this.currentMode = mode;
        },
        handleThemeChanged(isDark) {
            this.isDarkTheme = isDark;
        },
        async handleOrgChange(value) {
            // 找到組織物件
            const org = this.organizations.find(o => o.id === value);
            if (org) {
                // 等待 changeOrg 完成（包括 Supabase 保存）
                await this.fieldScope.changeOrg(org);
            }
            // 刷新頁面
            location.reload();
        }
    }
};
</script>

<style lang="stylus" scoped>
#navDrawerContainer
    overflow: hidden;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    padding: 15px;

    .user-avatar
        width: 32px
        height: 32px
        border-radius: 50%
        float: right
        cursor: pointer
        border: 2px solid #e0e0e0
        object-fit: cover

#navDrawer
    height: calc(100vh - 37px)
    display: flex
    flex-direction: column
    overflow: hidden
    width: 285px
    padding: 5px 15px
    margin: 0px -20px 0 -20px

    .mode-selection
        flex-shrink: 0
        padding: 12px 0
        margin: 0 -15px 0 -15px
        padding-left: 15px
        padding-right: 15px
        border-top: 1px solid #e0e0e0
        border-bottom: 1px solid #e0e0e0
        background-color: #f9f9f9

        .mode-buttons-grid
            display: grid
            grid-template-columns: 1fr 1fr 1fr
            gap: 8px

            button
                display: flex
                flex-direction: column
                align-items: center
                justify-content: center
                gap: 4px
                padding: 10px 8px
                border: 1px solid #ddd
                background-color: #fff
                border-radius: 6px
                cursor: pointer
                font-size: 12px
                font-weight: 500
                color: #666
                transition: all 0.2s ease

                &:hover
                    background-color: #f5f5f5
                    border-color: #b3d8ff
                    transform: translateY(-2px)
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08)

                &.active
                    background-color: #e6f7ff
                    border-color: #2d8cf0
                    color: #2d8cf0
                    font-weight: 600
                    box-shadow: 0 2px 8px rgba(45, 140, 240, 0.15)

                    :deep(.ivu-icon)
                        color: #2d8cf0

                    i.icofont
                        color: #2d8cf0

                    // Keep duotone after color in active state
                    i.icofont.icofont-duotone:after
                        color: #f39838 !important

                &.placeholder
                    opacity: 0.4
                    cursor: not-allowed
                    border-style: dashed

                    &:hover
                        transform: none
                        box-shadow: none
                        background-color: #fff
                        border-color: #ddd

                &.locked
                    opacity: 0.5
                    cursor: not-allowed
                    position: relative

                    &:hover
                        transform: none
                        box-shadow: none
                        background-color: #f5f5f5
                        border-color: #ddd

                    .lock-icon
                        position: absolute
                        top: 4px
                        right: 4px
                        font-size: 10px
                        color: #999

                i.icofont
                    font-size: 22px
                    color: #666
                    transition: color 0.2s ease
                    display: flex
                    align-items: center
                    justify-content: center

                    // Duotone color for after pseudo-element - only in mode-btn
                    &.icofont-duotone:after
                        color: #f39838 !important

                span
                    font-size: 11px
                    white-space: nowrap

    .org-select
        flex-shrink: 0
        border-top: 1px solid #ddd
        background-color: #f8f8f8
        padding: 12px 15px
        margin: 0px -15px 0

.org-option
    padding: 8px 0

.org-name
    font-size: 14px
    color: #333

:deep(.ivu-select-dropdown)
    .ivu-select-item
        padding: 8px 15px
        &:hover
            background-color: #f0f0f0
        &-selected
            color: #2d8cf0
            font-weight: 600
            background-color: #f0f0f0

.version-info
  flex-shrink: 0
  padding: 10px 0
  font-size: 10px
  color: #999
  display: flex
  flex-direction: column
  border: none !important
  background: transparent !important
  gap: 2px
  margin-bottom: 4px;

  span
    display: block

// ===== 暗色主題樣式 =====
// 定義暗色主題變數
$dark-bg = #1a1d24
$dark-bg-elevated = #252a33
$dark-bg-hover = #2e3440
$dark-border = #3b4252
$dark-border-light = #4c566a
$dark-text = #e5e9f0
$dark-text-secondary = #b8c0cc
$dark-text-muted = #8892a0
$dark-primary = #5A9FFF
$dark-primary-hover = #4A8FEF

// 整體容器暗色主題
#navDrawerContainer.dark-theme
    background: $dark-bg

    h1
        color: $dark-text

    .user-avatar
        border-color: $dark-border

// navDrawer 區域暗色主題
#navDrawerContainer.dark-theme #navDrawer
    // mode-selection 區域
    .mode-selection
        border-top-color: $dark-border
        background-color: $dark-bg-elevated

        .mode-buttons-grid
            button
                border-color: $dark-border
                background-color: $dark-bg
                color: $dark-text-secondary

                &:hover
                    background-color: $dark-bg-hover
                    border-color: $dark-primary
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3)

                &.active
                    background-color: rgba(90, 159, 255, 0.2)
                    border-color: $dark-primary
                    color: $dark-primary
                    box-shadow: 0 2px 8px rgba(90, 159, 255, 0.25)

                    :deep(.ivu-icon)
                        color: $dark-primary

                    i.icofont
                        color: $dark-primary

                &.placeholder
                    opacity: 0.3
                    cursor: not-allowed
                    border-style: dashed

                    &:hover
                        transform: none
                        box-shadow: none
                        background-color: $dark-bg
                        border-color: $dark-border

                &.locked
                    opacity: 0.4
                    cursor: not-allowed

                    &:hover
                        transform: none
                        box-shadow: none
                        background-color: $dark-bg-hover
                        border-color: $dark-border

                    .lock-icon
                        color: $dark-text-muted

                i.icofont
                    color: $dark-text-secondary

    // org-select 區域
    .org-select
        border-top-color: $dark-border
        border-bottom-color: $dark-border
        background-color: $dark-bg-elevated

        :deep(.ivu-select-selection)
            background-color: $dark-bg !important
            border-color: $dark-border !important

            .ivu-select-selected-value
                color: $dark-text !important

            .ivu-select-arrow
                color: $dark-text-secondary

    // version-info 區域
    .version-info
        color: $dark-text-muted
        background: transparent !important

// 下拉選單暗色主題（transfer 到 body 層的元素）
:deep(.ivu-select-dropdown)
    #navDrawerContainer.dark-theme &
        background: $dark-bg-elevated
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4)

        .ivu-select-item
            color: $dark-text

            &:hover
                background: $dark-bg-hover

            &-selected
                background: $dark-primary
                color: #fff

                &:hover
                    background: $dark-primary-hover
</style>